import { Component, EventEmitter, OnInit, OnDestroy, Output, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DmsService } from 'src/app/dms/dms.service';
import { DOCUMENT_TYPE_LIST, DocumentAction, DocumentTypeId } from 'src/app/Models/dmsModel';

@Component({
  selector: 'app-dms-add-imanage-doc',
  templateUrl: './dms-add-imanage-doc.component.html',
  styleUrls: ['./dms-add-imanage-doc.component.scss']
})

export class DmsAddImanageDocComponent implements OnInit, OnDestroy {
  @Output() closeAddImanageDocPopup = new EventEmitter();
  public bsModalRef: BsModalRef;
  @ViewChild("templateLocationPopup") public templateLocationPopup: any;
  private unsubscribe = new Subject<void>();
  clientGroupList: any[] = [];
  clientNameList: any[] = [];
  filteredClientGroupList: any[] = [];
  filteredClientNameList: any[] = [];
  selectedClientGroupCode: string | null = null;
  selectedClientId: number | null = null;
  selectedClientCode: string = '';
  selectedTemplate: any;
  templateName: string = '';
  fileName: string = '';
  fileIcon: string = '';
  isAddDisabled: boolean = true;
  isClientAutoSelectedFromRoute: boolean = false;
  documentTypeList = DOCUMENT_TYPE_LIST;
  documentTypeId = DocumentTypeId;
  documentType: number = DocumentTypeId.Merge; // Default to 'Create mail merge document'
  isNonSharePoint: boolean = false;
  constructor(public modalService: BsModalService, private dmsService: DmsService, private toastr: ToastrService, private route: ActivatedRoute) { }

  ngOnInit(): void {
    const dmsProviderType = localStorage.getItem('DMSProviderType');
    this.isNonSharePoint = dmsProviderType !== null && dmsProviderType !== 'SharePoint';
    this.loadDMSClientList();
  }

  loadDMSClientList() {
    const clientStatus = '';
    const groupCode = null;
    this.dmsService.getDMSClientList(clientStatus, groupCode).subscribe({
      next: (response: any) => {
        const result = Array.isArray(response?.result) ? response.result : [];
        this.clientNameList = result;
        this.filteredClientNameList = result;
        this.clientGroupList = result
          .map(item => ({ clientGroup: item.clientGroup, clientGroupCode: item.clientGroupCode }))
          .filter((item, index, self) => item.clientGroupCode && self.findIndex(x => x.clientGroupCode === item.clientGroupCode) === index);
        this.filteredClientGroupList = [...this.clientGroupList];        
        this.autoSelectClientFromRoute();      
      },
      error: (error) => {
        console.error('Error loading DMS client list:', error);
        this.clientNameList = [];
        this.filteredClientNameList = [];
        this.clientGroupList = [];
        this.filteredClientGroupList = [];
      }
    });
  }

  onClientSelected(event: any) {
    if (!event) {
      this.filteredClientGroupList = [...this.clientGroupList];
      this.selectedClientGroupCode = null;
      this.selectedClientCode = '';
      this.updateAddButtonState();
      return;
    }

    const selected = event;
    this.selectedClientCode = selected?.clientCode ?? '';
    
    // Filter groups based on selected client
    if (selected?.clientGroupCode) {
      // If client has a group, show only that group
      this.filteredClientGroupList = this.clientGroupList.filter(
        group => group.clientGroupCode === selected.clientGroupCode
      );
      this.selectedClientGroupCode = selected.clientGroupCode;
    } else {
      this.filteredClientGroupList = [];
      this.selectedClientGroupCode = null;
    }
    
    this.updateAddButtonState();
  }

  onGroupSelected(event: any) {
    if (!event) {
      this.selectedClientGroupCode = null;
      this.filteredClientNameList = [...this.clientNameList];
      this.filteredClientGroupList = [...this.clientGroupList];
      this.selectedClientId = null;
      this.selectedClientCode = '';
      this.updateAddButtonState();
      return;
    }

    const selectedGroupCode = event?.clientGroupCode || event;
    
    // Filter clients based on selected group - only show clients with matching group
    if (selectedGroupCode) {
      this.filteredClientNameList = this.clientNameList.filter(
        client => client.clientGroupCode === selectedGroupCode
      );
    } else {
      this.filteredClientNameList = [];
    }
    
    this.selectedClientId = null;
    this.selectedClientCode = '';
    this.updateAddButtonState();
  }

  updateAddButtonState(): void {
    if (this.documentType === DocumentTypeId.Merge) {
      this.isAddDisabled = !this.selectedTemplate || !this.selectedClientId;
    } else {
      this.isAddDisabled = !this.selectedTemplate;
    }
  }

  autoSelectClientFromRoute(): void {
    this.route.queryParams.pipe(takeUntil(this.unsubscribe)).subscribe(params => {
      const clientIdFromRoute = params['clientId'];
      if (clientIdFromRoute) {
        const clientIdNum = Number(clientIdFromRoute);
        const selectedClient = this.clientNameList.find(client => client.clientId === clientIdNum);
        
        if (selectedClient) {
          this.selectedClientId = clientIdNum;
          this.selectedClientCode = selectedClient?.clientCode ?? '';
          
          if (selectedClient?.clientGroupCode) {
            this.filteredClientGroupList = this.clientGroupList.filter(
              group => group.clientGroupCode === selectedClient.clientGroupCode
            );
            this.selectedClientGroupCode = selectedClient.clientGroupCode;
          } else {
            this.filteredClientGroupList = [];
            this.selectedClientGroupCode = null;
          }
          
          this.isClientAutoSelectedFromRoute = true;
          this.updateAddButtonState();
        }
      }
    });
  }

  closeAddDocPopup() {
    this.isAddDisabled = true;
    this.selectedTemplate = null;
    this.selectedClientId = null;
    this.selectedClientGroupCode = null;
    this.selectedClientCode = '';
    this.isClientAutoSelectedFromRoute = false;
    this.filteredClientNameList = [...this.clientNameList];
    this.filteredClientGroupList = [...this.clientGroupList];
    this.closeAddImanageDocPopup.emit();
  }

  openTemplateLocationPopup() {
    this.bsModalRef = this.modalService.show(this.templateLocationPopup, {
      class: `modal-dialog-centered cmn-modal-dsgn modal-lg`,
      backdrop: "static",
      keyboard: false
    });
  }

  hideTemplateLocationPopUp() {
    this.bsModalRef.hide();
  }

  onTemplateSelected(template: any) {
    this.selectedTemplate = template;
    const path = template.fullBlobPath;

    this.templateName = path.substring(0, path.lastIndexOf('/'));

    this.fileName = template.nameWithoutExtension || template.name;
    this.fileIcon = this.dmsService.getImageForFile(template.name);
    this.updateAddButtonState();
    this.hideTemplateLocationPopUp();
  }

  addDocument() {
    const payload = {
      clientId: this.selectedClientId ?? 0,
      blobPath: this.selectedTemplate.fullBlobPath,
      TemplateName: this.fileName,
      documentAction: this.getDocumentAction(this.documentType)
    };

    this.dmsService.downloadTemplates(payload)
      .subscribe({
        next: (response: any) => {
          const blob = response.body as Blob;
          
          // Generate filename with format: <TemplateName>_<ClientCode>_<YYYYMMDD>.<extension>
          const today = new Date();
          const dateString = today.getFullYear().toString() + 
                           String(today.getMonth() + 1).padStart(2, '0') + 
                           String(today.getDate()).padStart(2, '0');
          
          const templateName = this.selectedTemplate.name;
          const fileExtension = templateName.substring(templateName.lastIndexOf('.')) || '.docx';
          
          const downloadFileName = `${this.fileName}_${this.selectedClientCode}_${dateString}${fileExtension}`;

          this.triggerDownload(blob, downloadFileName);
          this.closeAddDocPopup();
        },

        error: (err) => {
          const message = err?.error?.message || err?.message || 'Download failed';
          this.toastr.error(message, 'Error');
        }
      });
  }

  triggerDownload(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  getDocumentAction(documentTypeId: number): DocumentAction {
    return documentTypeId === DocumentTypeId.Merge ? DocumentAction.Merge : DocumentAction.Copy;
  }

  onDocumentTypeChange(): void {
    this.selectedTemplate = null;
    this.templateName = '';
    this.fileName = '';
    this.fileIcon = '';

    if (this.documentType === DocumentTypeId.Copy) {
      this.selectedClientId = null;
      this.selectedClientGroupCode = null;
      this.selectedClientCode = '';
    }

    this.updateAddButtonState();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
} 
